import React, { useState, useEffect } from 'react';
import { getOptionsStrikePrices } from '../../services/api';

/**
 * StrikePriceSelect - Dynamic dropdown for selecting options strike prices
 *
 * Fetches available strike prices for a given ticker, option type, and expiration
 * from Polygon.io API and displays them in intelligent order (ATM first, expanding outward).
 *
 * Props:
 *   - ticker: Stock ticker symbol (e.g., 'AAPL')
 *   - optionType: 'call' or 'put'
 *   - expiration: Expiration date (YYYY-MM-DD format)
 *   - value: Currently selected strike price
 *   - onChange: Callback function when selection changes
 *   - disabled: Whether the dropdown should be disabled
 */
function StrikePriceSelect({ ticker, optionType, expiration, value, onChange, disabled }) {
  const [strikes, setStrikes] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [atmStrike, setAtmStrike] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if all prerequisites are met
  const hasPrerequisites = ticker && ticker.trim() !== '' &&
                          optionType &&
                          expiration && expiration.trim() !== '';

  // Fetch strike prices whenever ticker, optionType, or expiration changes
  useEffect(() => {
    // Clear data if prerequisites not met
    if (!hasPrerequisites) {
      setStrikes([]);
      setCurrentPrice(null);
      setAtmStrike(null);
      setError(null);
      return;
    }

    const fetchStrikePrices = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log(`📊 Fetching strike prices for ${ticker} ${optionType} ${expiration}...`);
        const data = await getOptionsStrikePrices(ticker, optionType, expiration);

        if (data.strikes && data.strikes.length > 0) {
          setStrikes(data.strikes);
          setCurrentPrice(data.current_price);
          setAtmStrike(data.atm_strike);
          console.log(`✅ Found ${data.strikes.length} strike prices for ${ticker}`);
          console.log(`📈 Current price: $${data.current_price}, ATM strike: $${data.atm_strike}`);
        } else {
          setStrikes([]);
          setCurrentPrice(data.current_price || null);
          setAtmStrike(null);
          setError(`No strike prices found for ${ticker} ${optionType} ${expiration}`);
          console.warn(`⚠️ No strike prices found`);
        }
      } catch (err) {
        console.error('❌ Error fetching strike prices:', err);
        setError(`Failed to fetch strike prices`);
        setStrikes([]);
        setCurrentPrice(null);
        setAtmStrike(null);
      } finally {
        setLoading(false);
      }
    };

    fetchStrikePrices();
  }, [ticker, optionType, expiration, hasPrerequisites]);

  /**
   * Calculate percentage difference from current price
   * Positive = OTM for calls / ITM for puts
   * Negative = ITM for calls / OTM for puts
   */
  const getStrikePercentage = (strike) => {
    if (!currentPrice || currentPrice === 0) return null;
    const diff = ((strike - currentPrice) / currentPrice) * 100;
    return diff;
  };

  /**
   * Get label for strike based on moneyness (ITM/ATM/OTM)
   */
  const getStrikeLabel = (strike) => {
    if (strike === atmStrike) {
      return 'ATM';
    }

    if (!currentPrice) return '';

    const pctDiff = getStrikePercentage(strike);
    if (pctDiff === null) return '';

    const absDiff = Math.abs(pctDiff);

    // For calls: strike > current = OTM, strike < current = ITM
    // For puts: strike < current = OTM, strike > current = ITM
    const isOTM = optionType === 'call' ? strike > currentPrice : strike < currentPrice;

    if (absDiff < 2.5) {
      return 'Near ATM';
    } else if (absDiff < 7.5) {
      return isOTM ? 'Slightly OTM' : 'Slightly ITM';
    } else if (absDiff < 15) {
      return isOTM ? 'OTM' : 'ITM';
    } else {
      return isOTM ? 'Deep OTM' : 'Deep ITM';
    }
  };

  /**
   * Format strike for display in dropdown
   */
  const formatStrikeDisplay = (strike) => {
    const label = getStrikeLabel(strike);
    const pct = getStrikePercentage(strike);

    let display = `$${strike.toFixed(2)}`;

    if (label) {
      display += ` (${label}`;
      if (pct !== null && label !== 'ATM') {
        const sign = pct > 0 ? '+' : '';
        display += ` ${sign}${pct.toFixed(1)}%`;
      }
      display += ')';
    }

    // Add checkmark for ATM strike
    if (strike === atmStrike) {
      display = `✓ ${display}`;
    }

    return display;
  };

  // Show prerequisite message if not all fields are selected
  if (!hasPrerequisites) {
    return (
      <div className="relative">
        <select disabled className="input opacity-60">
          <option>Select ticker, type & expiration first</option>
        </select>
        <small className="text-xs text-yellow-400 mt-1 block">
          ⚠️ Required: Ticker, Option Type, and Expiration
        </small>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Strike Price Dropdown - No spacing above */}
      <select
        className="input"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || loading || strikes.length === 0}
      >
        {/* Default placeholder option */}
        <option value="">
          {loading
            ? 'Loading strikes...'
            : strikes.length === 0
            ? 'No strikes available'
            : 'Select strike price'}
        </option>

        {/* Render strike prices */}
        {strikes.map((strike) => (
          <option key={strike} value={strike}>
            {formatStrikeDisplay(strike)}
          </option>
        ))}
      </select>

      {/* Helper text - Fixed height to prevent layout shift */}
      <div className="h-5 mt-1">
        {/* Error message display */}
        {error && !loading && (
          <small className="text-xs text-red-400 block">{error}</small>
        )}

        {/* Loading indicator */}
        {loading && (
          <small className="text-xs text-gray-400 block">
            Fetching available strike prices...
          </small>
        )}

        {/* Helper text when strikes are loaded - Shows strike count and current price */}
        {!loading && !error && strikes.length > 0 && (
          <small className="text-xs text-gray-400 block">
            {strikes.length} strike{strikes.length !== 1 ? 's' : ''} available
            {currentPrice && ` • Current: $${currentPrice.toFixed(2)}`}
          </small>
        )}
      </div>
    </div>
  );
}

export default StrikePriceSelect;
